import { useState, useEffect, useRef, useCallback } from 'react'
import { sessionHeartbeatApi } from '@common/api/services/sessionHeartbeat'
import { useAuth } from '@common/hooks/useAuth'

/**
 * Session timeout hook — replaces AngularJS session.js (292 lines).
 *
 * LEGACY: slu-frontend/src/common/security/session.js
 *
 * Tracks user activity via global DOM event listeners and sends
 * heartbeat pings to the backend. Shows a warning modal with
 * countdown when the user has been idle for 13 minutes, and
 * automatically logs out after 15 minutes of total inactivity.
 */

// ---------------------------------------------------------------------------
// Configuration (matching AngularJS session.js exactly)
// ---------------------------------------------------------------------------
const SESSION_TIMEOUT_MS = 15 * 60 * 1000 // 15 minutes
const WARNING_TIME_MS = 13 * 60 * 1000 // 13 minutes — show warning after this idle period
const HEARTBEAT_DEBOUNCE_MS = 30 * 1000 // 30 seconds — sliding debounce window
const HEARTBEAT_MAX_WAIT_MS = 14 * 60 * 1000 // 14 minutes — auto-fire if continuously active
const ACTIVITY_DEBOUNCE_MS = 500 // 500ms — debounce activity handler
const INACTIVITY_CHECK_INTERVAL_MS = 30 * 1000 // 30 seconds — poll for inactivity
const ACTIVITY_PING_INTERVAL_MS = 1 * 60 * 1000 // 1 minute — direct ping interval
const COUNTDOWN_SECONDS = 120 // 2 minutes countdown in warning dialog

// DOM events that count as user activity
const ACTIVITY_EVENTS: (keyof DocumentEventMap)[] = [
    'click',
    'keydown',
    'keyup',
    'keypress',
    'input',
    'change',
    'submit',
    'focus',
    'mousedown',
    'mouseup',
    'scroll',
]

// ---------------------------------------------------------------------------
// Custom debounce with leading, trailing, and maxWait support
// ---------------------------------------------------------------------------
function createDebouncedHeartbeat(
    fn: () => void,
    wait: number,
    maxWait: number,
): { invoke: () => void; cancel: () => void } {
    let timeoutId: ReturnType<typeof setTimeout> | null = null
    let maxTimeoutId: ReturnType<typeof setTimeout> | null = null
    let lastInvokeTime = 0

    const invoke = () => {
        lastInvokeTime = Date.now()
        if (timeoutId !== null) clearTimeout(timeoutId)
        if (maxTimeoutId !== null) clearTimeout(maxTimeoutId)
        timeoutId = null
        maxTimeoutId = null
        fn()
    }

    const debounced = () => {
        const now = Date.now()

        // Leading edge: fire immediately if enough time has passed
        if (now - lastInvokeTime >= wait) {
            invoke()
            return
        }

        // Trailing edge: schedule call at end of window
        if (timeoutId !== null) clearTimeout(timeoutId)
        timeoutId = setTimeout(invoke, wait)

        // Max wait: ensure we fire even if continuously triggered
        if (maxTimeoutId === null) {
            const timeSinceLastInvoke = now - lastInvokeTime
            const remaining = maxWait - timeSinceLastInvoke
            if (remaining > 0) {
                maxTimeoutId = setTimeout(invoke, remaining)
            }
        }
    }

    const cancel = () => {
        if (timeoutId !== null) clearTimeout(timeoutId)
        if (maxTimeoutId !== null) clearTimeout(maxTimeoutId)
        timeoutId = null
        maxTimeoutId = null
    }

    return { invoke: debounced, cancel }
}

// ---------------------------------------------------------------------------
// Format seconds as M:SS
// ---------------------------------------------------------------------------
function formatCountdown(totalSeconds: number): string {
    const minutes = Math.floor(totalSeconds / 60)
    const seconds = totalSeconds % 60
    return `${minutes}:${seconds.toString().padStart(2, '0')}`
}

// ---------------------------------------------------------------------------
// Hook
// ---------------------------------------------------------------------------
export interface UseSessionTimeoutReturn {
    showWarning: boolean
    countdownSeconds: number
    timeDisplay: string
    onContinue: () => void
    onLogout: () => void
}

export function useSessionTimeout(enabled: boolean): UseSessionTimeoutReturn {
    const { logout } = useAuth()

    // State
    const [showWarning, setShowWarning] = useState(false)
    const [countdownSeconds, setCountdownSeconds] = useState(COUNTDOWN_SECONDS)

    // Refs for mutable values that don't need re-renders
    const lastActivityTimeRef = useRef(Date.now())
    const lastPingTimeRef = useRef(0)
    const showWarningRef = useRef(false)
    const countdownIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null)
    const inactivityIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null)
    const activityDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null)
    const heartbeatRef = useRef<{ invoke: () => void; cancel: () => void } | null>(null)

    // Keep the ref in sync with state so event handlers see the current value
    useEffect(() => {
        showWarningRef.current = showWarning
    }, [showWarning])

    // -----------------------------------------------------------------------
    // Heartbeat ping
    // -----------------------------------------------------------------------
    const pingBackend = useCallback(() => {
        sessionHeartbeatApi.get().catch(() => {
            // Swallow errors — heartbeat failures shouldn't crash the app
        })
    }, [])

    // -----------------------------------------------------------------------
    // Logout handler
    // -----------------------------------------------------------------------
    const handleLogout = useCallback(() => {
        logout('session_timeout')
    }, [logout])

    // -----------------------------------------------------------------------
    // Continue handler — reset everything and dismiss warning
    // -----------------------------------------------------------------------
    const handleContinue = useCallback(() => {
        lastActivityTimeRef.current = Date.now()
        lastPingTimeRef.current = Date.now()
        setShowWarning(false)
        setCountdownSeconds(COUNTDOWN_SECONDS)

        if (countdownIntervalRef.current !== null) {
            clearInterval(countdownIntervalRef.current)
            countdownIntervalRef.current = null
        }

        // Ping backend immediately — the debounced heartbeat with leading=true
        // will fire right away since enough time has passed during the warning
        pingBackend()
    }, [pingBackend])

    // -----------------------------------------------------------------------
    // Main effect — sets up activity tracking, inactivity monitor, heartbeat
    // -----------------------------------------------------------------------
    useEffect(() => {
        if (!enabled) return

        // Initialize timestamps
        lastActivityTimeRef.current = Date.now()
        lastPingTimeRef.current = 0

        // Create debounced heartbeat
        const heartbeat = createDebouncedHeartbeat(
            pingBackend,
            HEARTBEAT_DEBOUNCE_MS,
            HEARTBEAT_MAX_WAIT_MS,
        )
        heartbeatRef.current = heartbeat

        // -------------------------------------------------------------------
        // Activity handler (debounced at ACTIVITY_DEBOUNCE_MS)
        // -------------------------------------------------------------------
        const handleActivity = () => {
            // Ignore activity while warning is showing
            if (showWarningRef.current) return

            lastActivityTimeRef.current = Date.now()

            // Debounced heartbeat
            heartbeat.invoke()

            // Direct ping if enough time since last ping
            const timeSinceLastPing = Date.now() - lastPingTimeRef.current
            if (timeSinceLastPing >= ACTIVITY_PING_INTERVAL_MS) {
                lastPingTimeRef.current = Date.now()
                pingBackend()
            }
        }

        // Debounced wrapper for DOM event listeners
        let debouncedActivityTimer: ReturnType<typeof setTimeout> | null = null
        const debouncedActivity = () => {
            if (debouncedActivityTimer !== null) clearTimeout(debouncedActivityTimer)
            debouncedActivityTimer = setTimeout(handleActivity, ACTIVITY_DEBOUNCE_MS)
        }
        activityDebounceRef.current = debouncedActivityTimer

        // -------------------------------------------------------------------
        // Visibility change — re-activate when tab becomes visible
        // -------------------------------------------------------------------
        const handleVisibilityChange = () => {
            if (document.visibilityState === 'visible') {
                handleActivity()
            }
        }

        // -------------------------------------------------------------------
        // Register global DOM listeners
        // -------------------------------------------------------------------
        for (const event of ACTIVITY_EVENTS) {
            document.addEventListener(event, debouncedActivity, { passive: true })
        }
        document.addEventListener('visibilitychange', handleVisibilityChange)

        // -------------------------------------------------------------------
        // Inactivity monitor (30-second interval)
        // -------------------------------------------------------------------
        inactivityIntervalRef.current = setInterval(() => {
            if (showWarningRef.current) return

            const elapsed = Date.now() - lastActivityTimeRef.current
            if (elapsed >= WARNING_TIME_MS) {
                setShowWarning(true)
            }
        }, INACTIVITY_CHECK_INTERVAL_MS)

        // -------------------------------------------------------------------
        // Cleanup
        // -------------------------------------------------------------------
        return () => {
            // Remove DOM listeners
            for (const event of ACTIVITY_EVENTS) {
                document.removeEventListener(event, debouncedActivity)
            }
            document.removeEventListener('visibilitychange', handleVisibilityChange)

            // Clear debounce timer
            if (debouncedActivityTimer !== null) clearTimeout(debouncedActivityTimer)

            // Clear inactivity monitor
            if (inactivityIntervalRef.current !== null) {
                clearInterval(inactivityIntervalRef.current)
                inactivityIntervalRef.current = null
            }

            // Clear countdown interval
            if (countdownIntervalRef.current !== null) {
                clearInterval(countdownIntervalRef.current)
                countdownIntervalRef.current = null
            }

            // Cancel pending heartbeat
            heartbeat.cancel()
            heartbeatRef.current = null
        }
    }, [enabled, pingBackend])

    // -----------------------------------------------------------------------
    // Countdown effect — starts when warning is shown
    // -----------------------------------------------------------------------
    useEffect(() => {
        if (!showWarning) {
            // Clear countdown if warning was dismissed
            if (countdownIntervalRef.current !== null) {
                clearInterval(countdownIntervalRef.current)
                countdownIntervalRef.current = null
            }
            return
        }

        // Reset countdown to full duration
        setCountdownSeconds(COUNTDOWN_SECONDS)

        countdownIntervalRef.current = setInterval(() => {
            setCountdownSeconds((prev) => {
                const next = prev - 1
                if (next <= 0) {
                    if (countdownIntervalRef.current !== null) {
                        clearInterval(countdownIntervalRef.current)
                        countdownIntervalRef.current = null
                    }
                    return 0
                }
                return next
            })
        }, 1000)

        return () => {
            if (countdownIntervalRef.current !== null) {
                clearInterval(countdownIntervalRef.current)
                countdownIntervalRef.current = null
            }
        }
    }, [showWarning, handleLogout])

    // -----------------------------------------------------------------------
    // Trigger logout when countdown reaches zero (side effect separated from state updater)
    // -----------------------------------------------------------------------
    useEffect(() => {
        if (showWarning && countdownSeconds <= 0) {
            handleLogout()
        }
    }, [showWarning, countdownSeconds, handleLogout])

    // -----------------------------------------------------------------------
    // Return values
    // -----------------------------------------------------------------------
    if (!enabled) {
        return {
            showWarning: false,
            countdownSeconds: COUNTDOWN_SECONDS,
            timeDisplay: formatCountdown(COUNTDOWN_SECONDS),
            onContinue: () => {},
            onLogout: () => {},
        }
    }

    return {
        showWarning,
        countdownSeconds,
        timeDisplay: formatCountdown(countdownSeconds),
        onContinue: handleContinue,
        onLogout: handleLogout,
    }
}
